// #413: lazy tree state for the UnifiedContainerSelector's 'all' (relationship/
// move target) mode. The picker used to eager-load the ENTIRE project tree on
// open (`getItems(true, projectId)`) — the same unbounded-payload problem #380
// fixed for the main tree. This is a deliberately small, picker-shaped sibling
// of useTreeState (which also writes the URL hash and wraps projects in PRJ-
// group nodes — both wrong inside a modal): bounded root page, children on
// expand, "Load more…" pagination nodes, open-to-current-node via the real
// ancestors endpoint, and SERVER-side search (so search spans the whole tree,
// not just loaded nodes) via the same pruned `searchItems` the main tree uses.
import { useCallback, useRef, useState } from 'react';

import { requirementsApi } from '../services/api';
import { makeLoadMoreNodes } from './useTreeState';

export const PICKER_PAGE_SIZE = 200;

const ROOT_KEY = '__picker_root__';

export default function usePickerTree({ projectId = null } = {}) {
  const [roots, setRoots] = useState([]);           // top-level page (+ load_more)
  const [childrenMap, setChildrenMap] = useState({}); // { [parentId]: node[] }
  const [expanded, setExpanded] = useState([]);
  const [loading, setLoading] = useState(false);     // initial root load
  const [loadingIds, setLoadingIds] = useState([]);  // per-node child fetches
  const [error, setError] = useState(null);
  // Server search: null = browsing; { roots, expand } = pruned result tree.
  const [searchResult, setSearchResult] = useState(null);
  const searchSeq = useRef(0);
  const loadedRef = useRef(new Set());

  const loadRoots = useCallback(async () => {
    setLoading(true);
    setError(null);
    setChildrenMap({});
    setSearchResult(null);
    loadedRef.current = new Set();
    try {
      const { items, page } = await requirementsApi.getItems(
        false, projectId, { paged: true, limit: PICKER_PAGE_SIZE });
      setRoots([
        ...(items || []),
        ...makeLoadMoreNodes(page, { parentKey: ROOT_KEY, projectId, via: 'root' }),
      ]);
      setExpanded([]);
    } catch (err) {
      console.error('Picker root load failed:', err);
      setError(err.message || 'Failed to load items');
      setRoots([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadChildren = useCallback(async (nodeOrId) => {
    const id = typeof nodeOrId === 'string' ? nodeOrId : nodeOrId?.id;
    if (!id || loadedRef.current.has(id)) return;
    loadedRef.current.add(id);
    setLoadingIds((prev) => [...prev, id]);
    try {
      const { items, page } = await requirementsApi.getChildren(
        id, { projectId, paged: true, limit: PICKER_PAGE_SIZE });
      setChildrenMap((prev) => ({
        ...prev,
        [id]: [
          ...(items || []),
          ...makeLoadMoreNodes(page, { parentKey: id, projectId, via: 'children' }),
        ],
      }));
    } catch (err) {
      console.error('Picker child load failed:', id, err);
      loadedRef.current.delete(id); // allow a retry on next expand
    } finally {
      setLoadingIds((prev) => prev.filter((x) => x !== id));
    }
  }, [projectId]);

  const loadMore = useCallback(async (node) => {
    if (!node || node._kind !== 'load_more') return;
    try {
      const opts = { paged: true, limit: PICKER_PAGE_SIZE, offset: node.offset };
      if (node.via === 'root') {
        const { items, page } = await requirementsApi.getItems(
          false, projectId, opts);
        setRoots((prev) => [
          ...prev.filter((n) => n._kind !== 'load_more'),
          ...(items || []),
          ...makeLoadMoreNodes(page, { parentKey: ROOT_KEY, projectId, via: 'root' }),
        ]);
      } else {
        const { items, page } = await requirementsApi.getChildren(
          node.parentKey, { projectId, ...opts });
        setChildrenMap((prev) => ({
          ...prev,
          [node.parentKey]: [
            ...(prev[node.parentKey] || []).filter((n) => n._kind !== 'load_more'),
            ...(items || []),
            ...makeLoadMoreNodes(page, { parentKey: node.parentKey, projectId, via: 'children' }),
          ],
        }));
      }
    } catch (err) {
      console.error('Picker load-more failed:', err);
    }
  }, [projectId]);

  // Open the picker showing an item's CURRENT location: load each level of its
  // (already known) ancestor chain root-first, then expand the chain. Falls back
  // to fetching the chain when only the item id is known.
  const openTo = useCallback(async (ancestorIds, itemId = null) => {
    let chain = ancestorIds;
    if ((!chain || !chain.length) && itemId) {
      try {
        ({ ancestors: chain } = await requirementsApi.getAncestors(itemId));
      } catch {
        chain = [];
      }
    }
    if (!chain || !chain.length) return;
    for (const aid of chain) {
      // Sequential on purpose: each level reveals the next expander.
       
      await loadChildren(aid);
    }
    setExpanded((prev) => Array.from(new Set([...prev, ...chain])));
  }, [loadChildren]);

  // Server-side search over the WHOLE (unloaded) tree; empty query returns to
  // browsing mode. Out-of-order responses are dropped (latest query wins).
  const search = useCallback(async (query) => {
    const q = (query || '').trim();
    const seq = (searchSeq.current += 1);
    if (!q) {
      setSearchResult(null);
      return;
    }
    try {
      const result = await requirementsApi.searchItems(q, projectId);
      if (searchSeq.current !== seq) return;
      setSearchResult({ roots: result.roots || [], expand: result.expand || [] });
    } catch (err) {
      console.error('Picker search failed:', err);
      if (searchSeq.current === seq) setSearchResult({ roots: [], expand: [] });
    }
  }, [projectId]);

  // Compose the nested items RequirementsTree renders: a lazily-loaded level in
  // childrenMap overrides, but recurse through inline children too (the #400
  // compose lesson from useTreeState).
  const compose = useCallback((node) => {
    if (!node || node._kind === 'load_more') return node;
    const kids = childrenMap[node.id] || node.children;
    return { ...node, children: (kids || []).map(compose) };
  }, [childrenMap]);

  const items = searchResult
    ? searchResult.roots
    : roots.map(compose);
  const effectiveExpanded = searchResult
    ? Array.from(new Set([...(searchResult.expand || []), ...expanded]))
    : expanded;

  return {
    items,
    expanded: effectiveExpanded,
    setExpanded,
    loading,
    loadingIds,
    error,
    searchActive: !!searchResult,
    loadRoots,
    loadChildren,
    loadMore,
    openTo,
    search,
  };
}
